"use client";

import { memo, useMemo } from "react";
import { ChevronDown, Loader2, Check, Zap } from "lucide-react";
import { ARIcon } from "./iconComponents/ARIcon";
import { colors as staticColors } from "@/static/colors";
import ColorSwatch from "./ui/ColorSwatch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { cn } from "@/lib/utils";

// --- Sub-components for better performance and organization ---

const FilterItemButton = memo(({ isSelected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      " px-4 md:px-6 py-1.5 rounded-lg text-sm  transition-all duration-75 border font-dana-regular bg-black/20",
      isSelected 
        ? "bg-[#101011] border-red-stroke text-white shadow-[0_0_15px_rgba(215,19,51,0.15)]" 
        : "border-transparent text-white/70 hover:border-white/20 hover:text-white"
    )}
  >
    {children}
  </button>
));
FilterItemButton.displayName = 'FilterItemButton';

const FilterSection = memo(({ value, title, count, children }) => (
  <AccordionItem value={value} className="border-none">
    <AccordionTrigger
      asHeading={false}
      className="w-full h-12 md:h-14 px-6 rounded-2xl bg-[#1D1E2066] hover:bg-[#1D1E2099] transition-all duration-200 flex items-center justify-between group py-0 border border-white/5 hover:border-white/10"
    >
      <span className="text-sm sm:text-base font-dana-medium text-white">{title}</span>
      <div className="flex items-center gap-3">
        {count > 0 && <span className="text-sm text-berry-500 font-dana-medium">{count} مورد</span>}
        <ChevronDown size={20} className="text-white/70 transition-transform duration-300 group-data-[state=open]:rotate-180" />
      </div>
    </AccordionTrigger>
    <AccordionContent className="mt-2 p-4 rounded-2xl bg-[#1D1E2033] border border-white/5">
      <div className="flex flex-wrap gap-1.5">
        {children}
      </div>
    </AccordionContent>
  </AccordionItem>
));
FilterSection.displayName = 'FilterSection';

export default function ProductFilters({ 
  onFilterChange, 
  onApply,
  onClear,
  selectedFilters = {},
  availableFilters = {},
  categories: propCategories = [],
  brands: propBrands = [],
  materials: propMaterials = [],
  className = "",
  isPending = false
}) {
  // Memoize static or derived data to avoid expensive recalculations on every render
  const colors = useMemo(() => availableFilters.colors || staticColors.map(c => ({
    id: c.code,
    name: c.title,
    linear: c.linear
  })), [availableFilters.colors]);

  const materials = useMemo(() => Array.isArray(propMaterials) ? propMaterials : [], [propMaterials]);
  const brands = useMemo(() => Array.isArray(propBrands) ? propBrands : [], [propBrands]);
  const categories = useMemo(() => Array.isArray(propCategories) ? propCategories : [], [propCategories]);

  // Toggle filter logic
  const toggleFilter = (type, value) => {
    const current = selectedFilters[type] || [];
    const valStr = String(value);
    const updated = current.includes(valStr)
      ? current.filter(item => item !== valStr)
      : [...current, valStr];
    
    onFilterChange({ ...selectedFilters, [type]: updated });
  };

  const toggleArFilter = () => {
    onFilterChange({ 
      ...selectedFilters, 
      ar: selectedFilters.ar === 'true' ? '' : 'true' 
    });
  };

  const toggleSmartFilter = () => {
    onFilterChange({ 
      ...selectedFilters, 
      is_smart: selectedFilters.is_smart === 'true' ? '' : 'true' 
    });
  };

  const hasAnyFilter = useMemo(() => Object.keys(selectedFilters).some(key => {
    if (key === 'q') return selectedFilters[key] !== "";
    if (key === 'ar') return selectedFilters[key] === "true";
    if (key === 'is_smart') return selectedFilters[key] === "true";
    return (selectedFilters[key] || []).length > 0;
  }), [selectedFilters]);

  return (
    <aside className={cn("w-full max-w-5xl mx-auto space-y-2   ", className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ">
        {/* AR Filter Checkbox */}
        <button
          type="button"
          onClick={toggleArFilter}
          className={cn(
            "w-full h-12 md:h-14 px-6 rounded-2xl flex items-center justify-between group transition-all duration-200 border bg-[#1D1E2066]",
            selectedFilters.ar === 'true'
              ? "border-red-stroke/50 shadow-[0_0_15px_rgba(215,19,51,0.1)]"
              : "border-white/5 hover:border-white/10"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-5 h-5 rounded-md border flex items-center justify-center transition-all duration-200",
              selectedFilters.ar === 'true' 
                ? "bg-red-stroke border-red-stroke" 
                : "border-white/20 bg-black/20 group-hover:border-white/40"
            )}>
              {selectedFilters.ar === 'true' && <Check size={14} className="text-white" />}
            </div>
            <span className="text-sm sm:text-base font-dana-medium text-white">قابلیت واقعیت افزوده (AR)</span>
          </div>
          <ARIcon size={24} className={cn(
            "transition-all duration-300",
            selectedFilters.ar === 'true' ? "text-red-stroke scale-110" : "text-white/40 group-hover:text-white/60"
          )} />
        </button>

        {/* Smart Filter Checkbox */}
        <button
          type="button"
          onClick={toggleSmartFilter}
          className={cn(
            "w-full h-12 md:h-14 px-6 rounded-2xl flex items-center justify-between group transition-all duration-200 border bg-[#1D1E2066]",
            selectedFilters.is_smart === 'true'
              ? "border-red-stroke/50 shadow-[0_0_15px_rgba(215,19,51,0.1)]"
              : "border-white/5 hover:border-white/10"
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-5 h-5 rounded-md border flex items-center justify-center transition-all duration-200",
              selectedFilters.is_smart === 'true' 
                ? "bg-red-stroke border-red-stroke" 
                : "border-white/20 bg-black/20 group-hover:border-white/40"
            )}>
              {selectedFilters.is_smart === 'true' && <Check size={14} className="text-white" />}
            </div>
            <span className="text-sm sm:text-base font-dana-medium text-white"> محصولات هوشمند</span>
          </div>
          <Zap size={22} className={cn(
            "transition-all duration-300",
            selectedFilters.is_smart === 'true' ? "text-red-stroke scale-110" : "text-white/40 group-hover:text-white/60"
          )} />
        </button>
      </div>

      <Accordion type="multiple" className="w-full space-y-2 ">
        {/* Category Filter */}
        {categories?.length > 0 && (
          <FilterSection 
            value="categories" 
            title="دسته‌بندی" 
            count={(selectedFilters.categories || []).length}
          >
            {categories.map((cat) => (
              <FilterItemButton
                key={cat.id}
                isSelected={(selectedFilters.categories || []).includes(String(cat.title_en))}
                onClick={() => toggleFilter('categories', cat.title_en)}
              >
                {cat.title || cat.name}
              </FilterItemButton>
            ))}
          </FilterSection>
        )}

        {/* Color Filter */}
        <FilterSection 
          value="color" 
          title="رنگ محصول" 
          count={(selectedFilters.colorCodes || []).length}
        >
          <TooltipProvider>
            {colors.map((color) => (
              <Tooltip key={color.id}>
                <TooltipTrigger asChild>
                  <ColorSwatch
                    code={color.id}
                    isSelected={(selectedFilters.colorCodes || []).includes(color.id)}
                    onClick={() => toggleFilter('colorCodes', color.id)}
                    className="cursor-pointer hover:scale-110 transition-transform"
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="font-dana text-xs">
                  {color.name}
                </TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </FilterSection>

        {/* Material Filter */}
        <FilterSection 
          value="material" 
          title="جنس محصول" 
          count={(selectedFilters.materialIds || []).length}
        >
          {materials.map((m) => (
            <FilterItemButton
              key={m.id}
              isSelected={(selectedFilters.materialIds || []).includes(String(m.id))}
              onClick={() => toggleFilter('materialIds', m.id)}
            >
              {m.title || m.name}
            </FilterItemButton>
          ))}
        </FilterSection>

        {/* Brand Filter */}
        <FilterSection 
          value="brand" 
          title="برند" 
          count={(selectedFilters.brandIds || []).length}
        >
          {brands.map((b) => (
            <FilterItemButton
              key={b.id}
              isSelected={(selectedFilters.brandIds || []).includes(String(b.id))}
              onClick={() => toggleFilter('brandIds', b.id)}
            >
              {b.title || b.name}
            </FilterItemButton>
          ))}
        </FilterSection>
      </Accordion>

      {/* Actions Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 md:gap-4 pt-2">
        <button
          onClick={onApply}
          disabled={isPending}
          className="w-full sm:flex-1 h-12 md:h-14 rounded-2xl bg-red-stroke hover:bg-berry-500/90 text-white font-dana-medium text-base transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_4px_20px_rgba(217,52,81,0.3)] hover:scale-[1.01] active:scale-[0.99] shrink-0"
        >
          {isPending && <Loader2 size={20} className="animate-spin" />}
          اعمال فیلترها
        </button>
        
        {hasAnyFilter && (
          <button
            onClick={onClear}
            disabled={isPending}
            className="w-full sm:flex-1 h-12 md:h-14 rounded-2xl bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 font-dana-medium text-base transition-all disabled:opacity-50 hover:text-white shrink-0"
          >
            حذف تمام فیلترها
          </button>
        )}
      </div>
    </aside>
  );
}
